import { useForm } from "@tellescope/forms";
import { TellescopeModelFromName, useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  PageTabs,
  PageTabsContent,
  PageTabsList,
  PageTabsTrigger,
  withKey,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { Calendar, Clock, FileText, Globe, Settings, Shield, Zap } from "lucide-react";
import { useState } from "react";
import { CalendarEventReminder } from "@tellescope/types-models";
import { InfoSection } from "./InfoSection";
import { RemindersSection } from "./RemindersSection";
import { ProductsSection } from "./ProductsSection";
import { TemplateFormDialogContentsFormConfig } from "./types";
import { IntegrationsSection } from "./IntegrationsSection";
import { PortalSection } from "./PortalSection";
import { AdvancedSection } from "./AdvancedSection";
import { CarePlanSection } from "./CarePlanSection";
import { convertStringToVideoIntegration } from "./constants";

export interface TemplateFormDialogContentsProps {
  template?: TellescopeModelFromName<"calendar_event_templates">;
  templateId?: string;
  onSave?: () => void;
  onCancel?: () => void;
  shouldDuplicate?: boolean;
}

export const TemplateFormDialogContents = withKey(
  ({ template: providedTemplate, templateId, onSave, onCancel, shouldDuplicate }: TemplateFormDialogContentsProps) => {
    const { t } = useTranslation("calendar");
    const templateQuery = useTellescopeQuery({
      model: "calendar_event_templates",
      method: "getOne",
      request: templateId ?? "",
      enabled: !providedTemplate && !!templateId,
    });
    const template = providedTemplate ?? templateQuery.data;
    const [selectedTab, setSelectedTab] = useState("info");

    const createTemplateMutation = useTellescopeMutation({
      model: "calendar_event_templates",
      method: "createOne",
    });
    const updateTemplateMutation = useTellescopeMutation({
      model: "calendar_event_templates",
      method: "updateOne",
    });

    const [newReminders, setNewReminders] = useState<CalendarEventReminder[]>();
    const resolvedReminders = newReminders ?? template?.reminders ?? [];

    const form = useForm<TemplateFormDialogContentsFormConfig>({
      fields: {
        title: {
          type: "text",
          initialValue:
            shouldDuplicate && template?.title
              ? t("{{originalTitle}} (copy)", { originalTitle: template?.title })
              : template?.title,
          required: true,
        },
        durationInMinutes: {
          type: "number",
          initialValue: template?.durationInMinutes,
          required: true,
        },
        color: {
          type: "color",
          initialValue: template?.color,
        },
        description: {
          type: "text",
          initialValue: template?.description,
        },
        enableVideoCall: {
          type: "checkbox",
          initialValue: template?.enableVideoCall,
        },
        publicRead: {
          type: "checkbox",
          initialValue: template?.publicRead,
        },
        confirmationEmailDisabled: {
          type: "checkbox",
          initialValue: template?.confirmationEmailDisabled,
        },
        confirmationSMSDisabled: {
          type: "checkbox",
          initialValue: template?.confirmationSMSDisabled,
        },
        "portalSettings.hideUsers": {
          type: "checkbox",
          initialValue: template?.portalSettings?.hideUsers,
        },
        requiresEnduser: {
          type: "checkbox",
          initialValue: template?.requiresEnduser,
        },
        enduserAttendeeLimit: {
          type: "number",
          initialValue: template?.enduserAttendeeLimit ?? 1,
        },
        bufferStartMinutes: {
          type: "number",
          initialValue: template?.bufferStartMinutes,
        },
        bufferEndMinutes: {
          type: "number",
          initialValue: template?.bufferEndMinutes,
        },
        preventCancelMinutesInAdvance: {
          type: "number",
          initialValue: template?.preventCancelMinutesInAdvance,
        },
        preventRescheduleMinutesInAdvance: {
          type: "number",
          initialValue: template?.preventRescheduleMinutesInAdvance,
        },
        replaceHostOnReschedule: {
          type: "checkbox",
          initialValue: template?.replaceHostOnReschedule ?? true,
        },
        tags: {
          type: "multiselect",
          initialValue: template?.tags ?? [],
        },
        instructions: {
          type: "textarea",
          initialValue: template?.instructions,
        },
        resendRemindersOnAttendeeAdd: {
          type: "checkbox",
          initialValue: template?.resendRemindersOnAttendeeAdd,
        },
        productIds: {
          type: "multiselect",
          initialValue: template?.productIds ?? [""],
        },
        carePlanForms: {
          type: "multiselect",
          initialValue: template?.carePlanForms ?? [],
        },
        carePlanFiles: {
          type: "multiselect",
          initialValue: template?.carePlanFiles ?? [],
        },
        carePlanContent: {
          type: "multiselect",
          initialValue: template?.carePlanContent ?? [],
        },
        carePlanTasks: {
          type: "multiselect",
          initialValue: template?.carePlanTasks ?? [],
        },
        sendIcsEmail: {
          type: "checkbox",
          initialValue: template?.sendIcsEmail,
        },
        videoIntegration: {
          type: "select",
          initialValue: template?.videoIntegration ?? "No Integration",
        },
        dontAutoSyncPatientToHealthie: {
          type: "checkbox",
          initialValue: template?.dontAutoSyncPatientToHealthie,
        },
        matchToHealthieTemplate: {
          type: "checkbox",
          initialValue: template?.matchToHealthieTemplate,
        },
        healthieInsuranceBillingEnabled: {
          type: "checkbox",
          initialValue: template?.healthieInsuranceBillingEnabled,
        },
        dontSyncToCanvas: {
          type: "checkbox",
          initialValue: template?.dontSyncToCanvas,
        },
        "canvasCoding.system": {
          type: "text",
          initialValue: template?.canvasCoding?.system,
        },
        "canvasCoding.code": {
          type: "text",
          initialValue: template?.canvasCoding?.code,
        },
        "canvasCoding.display": {
          type: "text",
          initialValue: template?.canvasCoding?.display,
        },
        "canvasReasonCoding.system": {
          type: "text",
          initialValue: template?.canvasReasonCoding?.system,
        },
        "canvasReasonCoding.code": {
          type: "text",
          initialValue: template?.canvasReasonCoding?.code,
        },
        "canvasReasonCoding.display": {
          type: "text",
          initialValue: template?.canvasReasonCoding?.display,
        },
        dontSyncToElation: {
          type: "checkbox",
          initialValue: template?.dontSyncToElation,
        },
        athenaDepartmentId: {
          type: "text",
          initialValue: template?.athenaDepartmentId,
        },
        athenaTypeId: {
          type: "text",
          initialValue: template?.athenaTypeId,
        },
        athenaBookingTypeId: {
          type: "text",
          initialValue: template?.athenaBookingTypeId,
        },
        generateAthenaTelehealthLink: {
          type: "checkbox",
          initialValue: template?.generateAthenaTelehealthLink,
        },
        createAndBookAthenaSlot: {
          type: "checkbox",
          initialValue: template?.createAndBookAthenaSlot,
        },
        displayTitle: {
          type: "text",
          initialValue: template?.displayTitle,
        },
        displayDescription: {
          type: "textarea",
          initialValue: template?.displayDescription,
        },
        requirePortalCancelReason: {
          type: "checkbox",
          initialValue: template?.requirePortalCancelReason,
        },
        preventCancelInPortal: {
          type: "checkbox",
          initialValue: template?.preventCancelInPortal,
        },
        preventRescheduleInPortal: {
          type: "checkbox",
          initialValue: template?.preventRescheduleInPortal,
        },
        archivedAt: {
          type: "checkbox",
          initialValue: !!template?.archivedAt,
        },
        apiOnly: {
          type: "checkbox",
          initialValue: template?.apiOnly,
        },
      },
      onSubmit: async (values) => {
        if (template && !shouldDuplicate) {
          await updateTemplateMutation.mutateAsync({
            id: template.id,
            ...values,
            archivedAt: values.archivedAt ? template.archivedAt ?? new Date() : "",
            videoIntegration: convertStringToVideoIntegration(values.videoIntegration),
            reminders: resolvedReminders,
            productIds: values.productIds.filter(Boolean),
            _options: {
              replaceObjectFields: true,
            },
          });
        } else {
          await createTemplateMutation.mutateAsync({
            ...values,
            archivedAt: values.archivedAt ? new Date() : "",
            videoIntegration: convertStringToVideoIntegration(values.videoIntegration),
            reminders: resolvedReminders,
            productIds: values.productIds.filter(Boolean),
          });
        }

        onSave?.();
      },
    });

    return (
      <DialogContent divided data-testid="template-form-dialog" className="sm:max-w-4xl">
        <DialogHeader className="px-0">
          <div className="space-y-6">
            <h2 className="px-6">{template && !shouldDuplicate ? t("Edit Template") : t("Add Template")}</h2>
            <PageTabs value={selectedTab} onValueChange={setSelectedTab}>
              <PageTabsList className="h-10 overflow-x-auto px-6">
                <PageTabsTrigger value="info" className="flex items-center gap-1">
                  <FileText size={12} />
                  {t("Info")}
                </PageTabsTrigger>
                <PageTabsTrigger value="reminders" className="flex items-center gap-1">
                  <Clock size={12} />
                  {t("Reminders")}
                </PageTabsTrigger>
                <PageTabsTrigger value="products" className="flex items-center gap-1">
                  <Calendar size={12} />
                  {t("Products")}
                </PageTabsTrigger>
                <PageTabsTrigger value="care-plan" className="flex items-center gap-1">
                  <Shield size={12} />
                  {t("Care Plan")}
                </PageTabsTrigger>
                <PageTabsTrigger value="integrations" className="flex items-center gap-1">
                  <Zap size={12} />
                  {t("Integrations")}
                </PageTabsTrigger>
                <PageTabsTrigger value="portal" className="flex items-center gap-1">
                  <Globe size={12} />
                  {t("Portal")}
                </PageTabsTrigger>
                <PageTabsTrigger value="advanced" className="flex items-center gap-1">
                  <Settings size={12} />
                  {t("Advanced")}
                </PageTabsTrigger>
              </PageTabsList>
              <form {...form.getFormProps()} className="p-6 overflow-y-auto" style={{ height: "min(80svh, 875px)" }}>
                <PageTabsContent value="info">
                  <InfoSection form={form} />
                </PageTabsContent>
                <PageTabsContent value="reminders">
                  <RemindersSection
                    form={form}
                    reminders={resolvedReminders}
                    setReminders={(newVal) => {
                      if (typeof newVal === "function") {
                        const calcVal = newVal(resolvedReminders);
                        setNewReminders(calcVal);
                        return;
                      }

                      setNewReminders(newVal);
                    }}
                  />
                </PageTabsContent>
                <PageTabsContent value="products">
                  <ProductsSection form={form} />
                </PageTabsContent>
                <PageTabsContent value="care-plan">
                  <CarePlanSection form={form} />
                </PageTabsContent>
                <PageTabsContent value="integrations">
                  <IntegrationsSection form={form} />
                </PageTabsContent>
                <PageTabsContent value="portal">
                  <PortalSection form={form} />
                </PageTabsContent>
                <PageTabsContent value="advanced">
                  <AdvancedSection form={form} />
                </PageTabsContent>
              </form>
            </PageTabs>
          </div>
        </DialogHeader>
        <DialogFooter className="bg-muted rounded-b-md">
          <Button variant="outline" onClick={onCancel} data-testid="template-form-cancel">
            {t("Cancel")}
          </Button>
          <Button
            onClick={() => form.submitForm()}
            className="font-light"
            disabled={template ? !form.isDirty && !form.isValid : !form.isValid}
            loading={updateTemplateMutation.isPending || createTemplateMutation.isPending}
            data-testid="template-form-submit"
          >
            {template && !shouldDuplicate ? t("Save Changes") : t("Create Template")}
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  },
  ({ template, templateId }) => template?.id ?? templateId ?? ""
);
